import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  LinearProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Edit,
  CheckCircleOutline,
  School,
  ChevronRight,
  PlayCircleOutline
} from '@mui/icons-material';
import { format, differenceInCalendarDays } from 'date-fns';
import { Session } from '../../types/sessions';
import StatusChip from '../common/StatusChip';

interface ActiveSessionCardProps {
  session: Session | null;
  onEdit: (session: Session) => void;
  onComplete: (session: Session) => void;
  onOpenSession: (session: Session) => void;
  onPromoteToNext: (session: Session) => void;
}

const ActiveSessionCard: React.FC<ActiveSessionCardProps> = ({
  session, onEdit, onComplete, onOpenSession, onPromoteToNext
}) => {
  const theme = useTheme();

  const stats = useMemo(() => {
    if (!session) return null;
    const start = new Date(session.startDate);
    const end = new Date(session.endDate);
    const now = new Date();
    const totalDays = Math.max(1, differenceInCalendarDays(end, start) + 1);
    const elapsed = Math.max(0, Math.min(totalDays, differenceInCalendarDays(now, start) + 1));
    const remaining = Math.max(0, differenceInCalendarDays(end, now));
    const pct = Math.round((elapsed / totalDays) * 1000) / 10;
    return { totalDays, elapsed, remaining, pct };
  }, [session]);

  if (!session) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.warning.main, 0.04)
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: alpha(theme.palette.warning.main, 0.14),
              color: theme.palette.warning.dark,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <School fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>No active session</Typography>
            <Typography variant="caption" color="text.secondary">
              Activate a session below to start enrolling students and marking attendance.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${theme.palette.primary.main}`
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5, lineHeight: 1 }}>
              Active session
            </Typography>
            <StatusChip status={{ kind: 'session', value: session.status }} />
          </Stack>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {session.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(session.startDate), 'MMM d, yyyy')} → {format(new Date(session.endDate), 'MMM d, yyyy')}
            {stats && stats.remaining > 0 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                · {stats.remaining} {stats.remaining === 1 ? 'day' : 'days'} remaining
              </Typography>
            )}
          </Typography>

          {stats && (
            <LinearProgress
              variant="determinate"
              value={Math.min(100, stats.pct)}
              sx={{
                mt: 1, height: 4, borderRadius: 2, maxWidth: 360,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '& .MuiLinearProgress-bar': { borderRadius: 2 }
              }}
            />
          )}
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button size="small" variant="text" startIcon={<Edit fontSize="small" />} onClick={() => onEdit(session)}>
            Edit
          </Button>
          <Button size="small" variant="text" color="inherit" startIcon={<CheckCircleOutline fontSize="small" />} onClick={() => onComplete(session)}>
            Complete
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayCircleOutline fontSize="small" />}
            onClick={() => onPromoteToNext(session)}
            sx={{ borderRadius: 2 }}
          >
            Promote
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            endIcon={<ChevronRight fontSize="small" />}
            onClick={() => onOpenSession(session)}
            sx={{ borderRadius: 2 }}
          >
            Open
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ActiveSessionCard;
